import { useState, useEffect, useMemo } from 'react'
import { api } from '../../../services/api'
import { useRateLimit } from '../../../hooks/useRateLimit'
import { RateLimitIndicator } from '../../../components/RateLimitIndicator'
import { AddButton, TableContainer, Table, Tr, Th, Td, Chip } from './styles'
import {
  ActionButtonsRow,
  SvgImage,
  Container,
  InputDiv,
  Card,
  Select,
  PageContainer,
  PageHeader,
  InfoCard,
  Badge,
  BackButton,
  Result,
} from '../../../styles/global'
import { FaPlusCircle, FaEdit, FaMinusCircle } from 'react-icons/fa'
import { Tooltip } from 'react-tooltip'
import { toast } from 'react-toastify'
import { FiArrowLeft, FiInfo } from 'react-icons/fi'

export function PlumbingPluvial() {
  const { canCalculate, updateFromHeaders, handleRateLimitError, fetchRateLimitStatus } = useRateLimit()

  const headerGradient = 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)'
  const areasType = useMemo(() => {
    return ['Superfície plana horizontal', 'Superfície inclinada', 'Manual']
  }, [])

  const gutterTypes = useMemo(() => {
    return ['Retangular', 'Circular']
  }, [])

  useEffect(() => {
    async function fetchData() {
      const response = await api.get('/plumbing/pluvial/data')
      setMaterials(response.data.materials)
      setSemiCircularGutters(response.data.semiCircularGutters)
      setCircularHorizontals(response.data.circularHorizontals)
      setSelectedMaterial(response.data.materials[0].name)
    }

    fetchData()
  }, [])

  const [a, setA] = useState('')
  const [b, setB] = useState('')
  const [h, setH] = useState('')
  const [materials, setMaterials] = useState([])
  const [selectedMaterial, setSelectedMaterial] = useState('')
  const [manualArea, setManualArea] = useState('')
  const [gutterB, setGutterB] = useState('')
  const [gutterH, setGutterH] = useState('')
  const [gutterD, setGutterD] = useState('Ø100')
  const [areas, setAreas] = useState([])
  const [gutters, setGutters] = useState([])
  const [gutterName, setGutterName] = useState('')
  const [gutterSlope, setGutterSlope] = useState(1)
  const [areaName, setAreaName] = useState('')
  const [verticals, setVerticals] = useState([])
  const [horizontals, setHorizontals] = useState([])
  const [verticalName, setVerticalName] = useState('')
  const [verticalQuantity, setVerticalQuantity] = useState(1)
  const [semiCircularGutters, setSemiCircularGutters] = useState([])
  const [circularHorizontals, setCircularHorizontals] = useState([])
  const [verticalDiameter, setVerticalDiameter] = useState('Ø100')
  const [horizontalName, setHorizontalName] = useState('')
  const [horizontalQuantity, setHorizontalQuantity] = useState(1)
  const [horizontalSlope, setHorizontalSlope] = useState('0,5%')
  const [horizontalDiameter, setHorizontalDiameter] = useState('Ø50')
  const [selectedAreaType, setSelectedAreaType] = useState(areasType[0])
  const [selectedGutterType, setSelectedGutterType] = useState(gutterTypes[0])
  const [rain, setRain] = useState('')
  const [isFormValid] = useState(true)
  const [result, setResult] = useState('')

  const uniqueGutterDiameters = useMemo(
    () => [...new Set(semiCircularGutters.map((g) => g.diameter))],
    [semiCircularGutters]
  )
  const uniqueGutterSlopes = useMemo(() => [...new Set(semiCircularGutters.map((g) => g.slope))], [semiCircularGutters])

  const uniqueHorizontalSlopes = useMemo(
    () => [...new Set(circularHorizontals.map((g) => g.slope))],
    [circularHorizontals]
  )
  const uniqueHorizontalDiameters = useMemo(
    () => [...new Set(circularHorizontals.map((g) => g.diameter))],
    [circularHorizontals]
  )

  const handleRemoveArea = (event, removeId) => {
    event.stopPropagation()

    const filteredAreas = areas.filter((area) => area.id !== removeId)

    const idMap = {}
    filteredAreas.forEach((area, idx) => {
      idMap[area.id] = idx + 1
    })

    const newAreas = filteredAreas.map((area, idx) => ({
      ...area,
      id: idx + 1,
    }))

    const newGutters = gutters.map((gutter) => {
      const newContribs = gutter.contributions.areasContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...gutter,
        contributions: {
          ...gutter.contributions,
          areasContributions: newContribs,
        },
      }
    })

    const newVerticals = verticals.map((vertical) => {
      const newContribs = vertical.contributions.areasContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...vertical,
        contributions: {
          ...vertical.contributions,
          areasContributions: newContribs,
        },
      }
    })

    const newHorizontals = horizontals.map((horizontal) => {
      const newContribs = horizontal.contributions.areasContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...horizontal,
        contributions: {
          ...horizontal.contributions,
          areasContributions: newContribs,
        },
      }
    })

    setAreas(newAreas)
    setGutters(newGutters)
    setVerticals(newVerticals)
    setHorizontals(newHorizontals)

    toast.success('Área removida com sucesso!')
  }

  const handleEditArea = (event, areaId) => {
    event.preventDefault()

    let areaValue
    if (selectedAreaType === areasType[0]) {
      areaValue = a * b
    } else if (selectedAreaType === areasType[1]) {
      areaValue = (a + h / 2) * b
    } else {
      areaValue = manualArea
    }
    const updatedArea = { id: areaId, name: areaName, area: areaValue }

    setAreas((prev) => prev.map((ar) => (ar.id === areaId ? updatedArea : ar)))

    setGutters((prev) =>
      prev.map((gutter) => ({
        ...gutter,
        contributions: {
          ...gutter.contributions,
          areasContributions: gutter.contributions.areasContributions.map((ac) =>
            ac.id === areaId ? updatedArea : ac
          ),
        },
      }))
    )

    setVerticals((prev) =>
      prev.map((vertical) => ({
        ...vertical,
        contributions: {
          areasContributions: vertical.contributions.areasContributions.map((ac) =>
            ac.id === areaId ? updatedArea : ac
          ),
          gutterContributions: vertical.contributions.gutterContributions.map((gc) => ({
            ...gc,
            contributions: {
              ...gc.contributions,
              areasContributions: gc.contributions.areasContributions.map((ac) =>
                ac.id === areaId ? updatedArea : ac
              ),
            },
          })),
        },
      }))
    )

    setHorizontals((prev) =>
      prev.map((horizontal) => ({
        ...horizontal,
        contributions: {
          areasContributions: horizontal.contributions.areasContributions.map((ac) =>
            ac.id === areaId ? updatedArea : ac
          ),

          gutterContributions: horizontal.contributions.gutterContributions.map((gc) => ({
            ...gc,
            contributions: {
              ...gc.contributions,
              areasContributions: gc.contributions.areasContributions.map((ac) =>
                ac.id === areaId ? updatedArea : ac
              ),
            },
          })),

          verticalContributions: horizontal.contributions.verticalContributions.map((vc) => ({
            ...vc,
            contributions: {
              areasContributions: vc.contributions.areasContributions.map((ac) =>
                ac.id === areaId ? updatedArea : ac
              ),

              gutterContributions: vc.contributions.gutterContributions.map((gc) => ({
                ...gc,
                contributions: {
                  ...gc.contributions,
                  areasContributions: gc.contributions.areasContributions.map((ac) =>
                    ac.id === areaId ? updatedArea : ac
                  ),
                },
              })),
            },
          })),
        },
      }))
    )

    toast.success('Área editada com sucesso!')
  }

  const handleAddArea = (event) => {
    event.preventDefault()

    const id = areas.length + 1

    let area
    if (selectedAreaType == areasType[0]) {
      area = a * b
    } else if (selectedAreaType == areasType[1]) {
      area = (a + h / 2) * b
    } else if (selectedAreaType == areasType[2]) {
      area = manualArea
    }

    toast.success('Área adicionada com sucesso!')

    setAreas([...areas, { id, name: areaName, area }])
  }

  const handleAddVertical = (event) => {
    event.preventDefault()

    setVerticals([
      ...verticals,
      {
        id: verticals.length + 1,
        name: verticalName,
        quantity: verticalQuantity,
        diameter: verticalDiameter,
        contributions: { areasContributions: [], gutterContributions: [] },
      },
    ])

    toast.success('Condutor vertical adicionado com sucesso!')
  }

  const handleEditVertical = (event, verticalId) => {
    event.preventDefault()
    event.stopPropagation()

    setVerticals((prev) =>
      prev.map((v) =>
        v.id === verticalId ? { ...v, name: verticalName, quantity: verticalQuantity, diameter: verticalDiameter } : v
      )
    )

    toast.success('Condutor vertical editado com sucesso!')
  }

  const handleRemoveVertical = (event, removeId) => {
    event.preventDefault()
    event.stopPropagation()

    const filteredVerticals = verticals.filter((vertical) => vertical.id !== removeId)

    const idMap = {}
    filteredVerticals.forEach((vertical, idx) => {
      idMap[vertical.id] = idx + 1
    })

    const newVerticals = filteredVerticals.map((vertical, idx) => ({
      ...vertical,
      id: idx + 1,
    }))

    const newHorizontals = horizontals.map((horizontal) => {
      const newContribs = horizontal.contributions.verticalContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...horizontal,
        contributions: {
          ...horizontal.contributions,
          verticalContributions: newContribs,
        },
      }
    })

    setVerticals(newVerticals)
    setHorizontals(newHorizontals)

    toast.success('Condutor vertical removido com sucesso!')
  }

  const handleAddHorizontal = (event) => {
    event.preventDefault()

    setHorizontals([
      ...horizontals,
      {
        id: horizontals.length + 1,
        name: horizontalName,
        quantity: horizontalQuantity,
        slope: horizontalSlope,
        diameter: horizontalDiameter,
        contributions: { areasContributions: [], gutterContributions: [], verticalContributions: [] },
      },
    ])

    toast.success('Condutor horizontal adicionado com sucesso!')
  }

  const handleEditHorizontal = (event, horizontalId) => {
    event.preventDefault()
    event.stopPropagation()

    setHorizontals((prev) =>
      prev.map((h) =>
        h.id === horizontalId
          ? {
              ...h,
              name: horizontalName,
              quantity: horizontalQuantity,
              slope: horizontalSlope,
              diameter: horizontalDiameter,
            }
          : h
      )
    )

    toast.success('Condutor horizontal editado com sucesso!')
  }

  const handleRemoveHorizontal = (event, removeId) => {
    event.preventDefault()
    event.stopPropagation()

    const idToRemove = parseInt(removeId, 10)

    setHorizontals((prev) =>
      prev.filter((h) => parseInt(h.id, 10) !== idToRemove).map((h, idx) => ({ ...h, id: idx + 1 }))
    )

    toast.success('Condutor horizontal removido com sucesso!')
  }

  const handleAddAreaContributionToGutter = (event, gutterId, areaId) => {
    event.stopPropagation()

    const area = areas.find((area) => area.id === areaId)

    setGutters((prevGutters) =>
      prevGutters.map((gutter) =>
        gutter.id === gutterId
          ? {
              ...gutter,
              contributions: {
                ...gutter.contributions,
                areasContributions: [...gutter.contributions.areasContributions, area],
              },
            }
          : gutter
      )
    )
  }

  const handleAddGutterContributionToVertical = (event, verticalId, gutterId) => {
    event.stopPropagation()

    const gutter = gutters.find((gutter) => gutter.id === gutterId)

    setVerticals((prevVerticals) =>
      prevVerticals.map((vertical) =>
        vertical.id === verticalId
          ? {
              ...vertical,
              contributions: {
                ...vertical.contributions,
                gutterContributions: [...vertical.contributions.gutterContributions, gutter],
              },
            }
          : vertical
      )
    )
  }

  const handleAddGutterContributionToHorizontal = (event, horizontalId, gutterId) => {
    event.stopPropagation()

    const gutter = gutters.find((gutter) => gutter.id === gutterId)

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                gutterContributions: [...horizontal.contributions.gutterContributions, gutter],
              },
            }
          : horizontal
      )
    )
  }

  const handleAddVerticalContributionToHorizontal = (event, horizontalId, verticalId) => {
    event.stopPropagation()

    const vertical = verticals.find((vertical) => vertical.id === verticalId)

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                verticalContributions: [...horizontal.contributions.verticalContributions, vertical],
              },
            }
          : horizontal
      )
    )
  }

  const handleAddAreaContributionToVertical = (event, verticalId, areaId) => {
    event.stopPropagation()
    const area = areas.find((area) => area.id === areaId)

    setVerticals((prevVerticals) =>
      prevVerticals.map((vertical) =>
        vertical.id === verticalId
          ? {
              ...vertical,
              contributions: {
                ...vertical.contributions,
                areasContributions: [...vertical.contributions.areasContributions, area],
              },
            }
          : vertical
      )
    )
  }

  const handleAddAreaContributionToHorizontal = (event, horizontalId, areaId) => {
    event.stopPropagation()
    const area = areas.find((area) => area.id === areaId)

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                areasContributions: [...horizontal.contributions.areasContributions, area],
              },
            }
          : horizontal
      )
    )
  }

  const handleRemoveAreaContributionFromGutter = (event, gutterId, contributionId) => {
    event.stopPropagation()
    setGutters((prevGutters) =>
      prevGutters.map((gutter) =>
        gutter.id === gutterId
          ? {
              ...gutter,
              contributions: {
                ...gutter.contributions,
                areasContributions: gutter.contributions.areasContributions.filter((c) => c.id !== contributionId),
              },
            }
          : gutter
      )
    )
  }

  const handleRemoveAreaContributionFromHorizontal = (event, horizontalId, contributionId) => {
    event.stopPropagation()

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                areasContributions: horizontal.contributions.areasContributions.filter((c) => c.id !== contributionId),
              },
            }
          : horizontal
      )
    )
  }

  const handleRemoveGutterContributionFromHorizontal = (event, horizontalId, contributionId) => {
    event.stopPropagation()

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                gutterContributions: horizontal.contributions.gutterContributions.filter(
                  (c) => c.id !== contributionId
                ),
              },
            }
          : horizontal
      )
    )
  }

  const handleRemoveVerticalContributionFromHorizontal = (event, horizontalId, contributionId) => {
    event.stopPropagation()

    setHorizontals((prevHorizontals) =>
      prevHorizontals.map((horizontal) =>
        horizontal.id === horizontalId
          ? {
              ...horizontal,
              contributions: {
                ...horizontal.contributions,
                verticalContributions: horizontal.contributions.verticalContributions.filter(
                  (c) => c.id !== contributionId
                ),
              },
            }
          : horizontal
      )
    )
  }

  const handleRemoveAreaContributionFromVertical = (event, verticalId, contributionId) => {
    event.stopPropagation()

    setVerticals((prevVerticals) =>
      prevVerticals.map((vertical) =>
        vertical.id === verticalId
          ? {
              ...vertical,
              contributions: {
                ...vertical.contributions,
                areasContributions: vertical.contributions.areasContributions.filter((c) => c.id !== contributionId),
              },
            }
          : vertical
      )
    )
  }

  const handleRemoveGutterContributionFromVertical = (event, verticalId, contributionId) => {
    event.stopPropagation()

    setVerticals((prevVerticals) =>
      prevVerticals.map((vertical) =>
        vertical.id === verticalId
          ? {
              ...vertical,
              contributions: {
                ...vertical.contributions,
                gutterContributions: vertical.contributions.gutterContributions.filter((c) => c.id !== contributionId),
              },
            }
          : vertical
      )
    )
  }

  const handleAddGutter = (event) => {
    event.preventDefault()

    const id = gutters.length + 1

    const dimensions = selectedGutterType == gutterTypes[0] ? { b: gutterB, h: gutterH } : { d: gutterD }

    const dimension = selectedGutterType == gutterTypes[0] ? `${gutterB} x ${gutterH}` : `${gutterD}`

    const material = materials.find((m) => m.name === selectedMaterial)

    setGutters([
      ...gutters,
      {
        id,
        name: gutterName,
        material,
        slope: gutterSlope,
        type: selectedGutterType,
        dimensions,
        dimension,
        contributions: { areasContributions: [] },
      },
    ])

    toast.success('Calha adicionada com sucesso!')
  }

  const handleEditGutter = (event, gutterId) => {
    event.preventDefault()
    event.stopPropagation()

    const material = materials.find((m) => m.name === selectedMaterial)

    const dimensions = selectedGutterType === gutterTypes[0] ? { b: gutterB, h: gutterH } : { d: gutterD }

    const dimension = selectedGutterType === gutterTypes[0] ? `${gutterB} x ${gutterH}` : `${gutterD}`

    setGutters((prev) =>
      prev.map((g) =>
        g.id === gutterId
          ? { ...g, name: gutterName, material, slope: gutterSlope, type: selectedGutterType, dimensions, dimension }
          : g
      )
    )

    toast.success('Calha editada com sucesso!')
  }

  const handleRemoveGutter = (event, removeId) => {
    event.preventDefault()
    event.stopPropagation()

    const filteredGutters = gutters.filter((gutter) => gutter.id !== removeId)

    const idMap = {}
    filteredGutters.forEach((gutter, idx) => {
      idMap[gutter.id] = idx + 1
    })

    const newGutters = filteredGutters.map((gutter, idx) => ({
      ...gutter,
      id: idx + 1,
    }))

    const newVerticals = verticals.map((vertical) => {
      const newContribs = vertical.contributions.gutterContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...vertical,
        contributions: {
          ...vertical.contributions,
          gutterContributions: newContribs,
        },
      }
    })

    const newHorizontals = horizontals.map((horizontal) => {
      const newContribs = horizontal.contributions.gutterContributions
        .filter((contrib) => contrib.id !== removeId)
        .map((contrib) => ({
          ...contrib,
          id: idMap[contrib.id],
        }))

      return {
        ...horizontal,
        contributions: {
          ...horizontal.contributions,
          gutterContributions: newContribs,
        },
      }
    })

    setGutters(newGutters)
    setVerticals(newVerticals)
    setHorizontals(newHorizontals)

    toast.success('Calha removida com sucesso!')
  }

  async function handleCalculate(event) {
    event.preventDefault()

    try {
      const data = {
        rain,
        areas,
        gutters,
        verticals,
        horizontals,
      }

      const response = await api.post('/plumbing/pluvial', data)
      setResult(response.data)

      if (response.rateLimit) {
        updateFromHeaders({
          'x-ratelimit-limit': response.rateLimit.limit.toString(),
          'x-ratelimit-remaining': response.rateLimit.remaining.toString(),
          'x-ratelimit-reset': response.rateLimit.reset,
        })
      }

      await fetchRateLimitStatus()

      toast.success('Cálculo realizado com sucesso!')
    } catch (error) {
      console.error('Erro ao calcular:', error)

      if (handleRateLimitError(error)) {
        toast.error(error.response?.data?.message || 'Você atingiu o limite de cálculos diários.')
        return
      }

      toast.error('Erro ao realizar o cálculo. Tente novamente.')
    }
  }

  function handleFileSave() {
    const stateData = {
      a,
      b,
      h,
      materials,
      selectedMaterial,
      manualArea,
      gutterB,
      gutterH,
      gutterD,
      areas,
      gutters,
      gutterName,
      gutterSlope,
      areaName,
      verticals,
      horizontals,
      verticalName,
      verticalQuantity,
      semiCircularGutters,
      circularHorizontals,
      verticalDiameter,
      horizontalName,
      horizontalQuantity,
      horizontalSlope,
      horizontalDiameter,
      selectedAreaType,
      selectedGutterType,
      rain,
      result,
    }
    const jsonString = JSON.stringify(stateData, null, 2)
    const blob = new Blob([jsonString], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'state.json'
    link.click()
    URL.revokeObjectURL(url)
  }

  function handleFileUpload(event) {
    const file = event.target.files[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (e) => {
      try {
        const jsonData = JSON.parse(e.target.result)

        setA(jsonData.a)
        setB(jsonData.b)
        setH(jsonData.h)
        setMaterials(jsonData.materials)
        setSelectedMaterial(jsonData.selectedMaterial)
        setManualArea(jsonData.manualArea)
        setGutterB(jsonData.gutterB)
        setGutterH(jsonData.gutterH)
        setGutterD(jsonData.gutterD)
        setAreas(jsonData.areas)
        setGutters(jsonData.gutters)
        setGutterName(jsonData.gutterName)
        setGutterSlope(jsonData.gutterSlope)
        setAreaName(jsonData.areaName)
        setVerticals(jsonData.verticals)
        setHorizontals(jsonData.horizontals)
        setVerticalName(jsonData.verticalName)
        setVerticalQuantity(jsonData.verticalQuantity)
        setSemiCircularGutters(jsonData.semiCircularGutters)
        setCircularHorizontals(jsonData.circularHorizontals)
        setVerticalDiameter(jsonData.verticalDiameter)
        setHorizontalName(jsonData.horizontalName)
        setHorizontalQuantity(jsonData.horizontalQuantity)
        setHorizontalSlope(jsonData.horizontalSlope)
        setHorizontalDiameter(jsonData.horizontalDiameter)
        setSelectedAreaType(jsonData.selectedAreaType)
        setSelectedGutterType(jsonData.selectedGutterType)
        setRain(jsonData.rain)
        setResult(jsonData.result)
      } catch (error) {
        console.error('Erro ao ler o arquivo JSON:', error)
      }
    }
    reader.readAsText(file)
  }

  const gutterDimensionsDiv = (() => {
    if (selectedGutterType == gutterTypes[0]) {
      return (
        <>
          <InputDiv>
            <span>b (cm)</span>
            <input
              type="number"
              value={gutterB}
              onChange={(event) => setGutterB(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddGutter(e)
                }
              }}
            />
          </InputDiv>
          <InputDiv>
            <span>h (cm)</span>
            <input
              type="number"
              value={gutterH}
              onChange={(event) => setGutterH(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddGutter(e)
                }
              }}
            />
          </InputDiv>
        </>
      )
    } else {
      return (
        <>
          <InputDiv>
            <span>Diâmetro</span>
            <div>
              <Select
                style={{ marginLeft: '0' }}
                id="gutterDiameterSelect"
                value={gutterD}
                onChange={(event) => setGutterD(event.target.value)}
              >
                {uniqueGutterDiameters.map((g, index) => (
                  <option key={index} value={g}>
                    {g}
                  </option>
                ))}
              </Select>
            </div>
          </InputDiv>
        </>
      )
    }
  })()

  const PlaneSurfaceImage = () => {
    return (
      <SvgImage viewBox="0 0 500 250" fill="none" xmlns="http://www.w3.org/2000/svg">
        <line x1="41.8015" y1="154.401" x2="210.415" y2="58.8531" stroke="black" strokeWidth="2" />
        <line x1="265.121" y1="154.401" x2="433.735" y2="58.8531" stroke="black" strokeWidth="2" />
        <line x1="210.159" y1="59.0092" x2="434.228" y2="59.0092" stroke="black" strokeWidth="2" />
        <line x1="42.2945" y1="154.271" x2="266.364" y2="154.271" stroke="black" strokeWidth="2" />
        <path
          d="M313.085 155.443L318.858 155.481L316.005 150.462L313.085 155.443ZM482.539 59.1212L476.766 59.0824L479.619 64.1017L482.539 59.1212ZM316.997 153.219L317.244 153.653L478.874 61.7796L478.627 61.345L478.38 60.9103L316.75 152.784L316.997 153.219Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="130.285" y="190.726">
            {a != 0 && a != '' ? a : 'a'}
          </tspan>
        </text>
        <line x1="271.267" y1="154.943" x2="326.76" y2="154.943" stroke="black" />
        <line x1="438.976" y1="58.6212" x2="487.296" y2="58.6212" stroke="black" />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
        >
          <tspan x="397.812" y="129.954">
            {b != 0 && b != '' ? b : 'b'}
          </tspan>
        </text>
        <line x1="37.8023" y1="157.228" x2="13.002" y2="175.629" stroke="black" />
        <line x1="263.03" y1="157.228" x2="238.23" y2="175.629" stroke="black" />
        <path
          d="M24.7459 167.215L29.7459 170.102L29.7459 164.328L24.7459 167.215ZM248.25 167.215L243.25 164.328L243.25 170.102L248.25 167.215ZM29.2459 167.215L29.2459 167.715L243.75 167.715L243.75 167.215L243.75 166.715L29.2459 166.715L29.2459 167.215Z"
          fill="black"
        />
      </SvgImage>
    )
  }

  const RoofSurfaceImage = () => {
    return (
      <SvgImage viewBox="0 0 500 250" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M221.704 114.247L98.5598 161.973L188.708 107.471L311.853 60.3346M221.704 114.247L346.91 164.919L435.291 112.479L311.853 60.3346M221.704 114.247L311.853 60.3346"
          stroke="black"
          strokeWidth="2"
        />
        <path d="M220.82 119.255V181.711V189.665M347.205 168.454V186.425" stroke="black" />
        <path
          d="M220.82 182.39L225.82 185.276V179.503L220.82 182.39ZM347.205 182.39L342.205 179.503V185.276L347.205 182.39ZM225.32 182.39V182.89H342.705V182.39V181.89H225.32V182.39Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="middle"
        >
          <tspan x="278.013" y="177.107">
            {a != 0 && a != '' ? a : 'a'}
          </tspan>
        </text>
        <line x1="214.633" y1="114.334" x2="39.9338" y2="114.334" stroke="black" />
        <line x1="95.0246" y1="162.473" x2="39.9338" y2="162.473" stroke="black" />
        <path
          d="M55.7531 113.834L52.8664 118.834L58.6399 118.834L55.7531 113.834ZM55.7531 161.973L58.6399 156.973L52.8664 156.973L55.7531 161.973ZM55.7531 118.334L55.2531 118.334L55.2531 157.473L55.7531 157.473L56.2531 157.473L56.2531 118.334L55.7531 118.334Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="end"
        >
          <tspan x="47.3292" y="145.176">
            {h != 0 && h != '' ? h : 'h'}
          </tspan>
        </text>
        <line x1="350.967" y1="166.241" x2="380.722" y2="181.389" stroke="black" />
        <line x1="439.349" y1="113.389" x2="462.899" y2="125.458" stroke="black" />
        <path
          d="M373.161 176.573L378.931 176.383L375.882 171.481L373.161 176.573ZM458.89 123.25L453.12 123.439L456.169 128.342L458.89 123.25ZM376.982 174.196L377.246 174.621L455.333 126.051L455.069 125.626L454.805 125.202L376.718 173.772L376.982 174.196Z"
          fill="black"
        />
        <text
          fill="black"
          xmlSpace="preserve"
          style={{ whiteSpace: 'pre' }}
          fontFamily="Poppins"
          fontSize="20"
          letterSpacing="0em"
          textAnchor="start"
        >
          <tspan x="420.562" y="166.959">
            {b != 0 && b != '' ? b : 'b'}
          </tspan>
        </text>
      </SvgImage>
    )
  }

  const areaCalculator = (() => {
    if (selectedAreaType == areasType[0]) {
      return (
        <>
          <PlaneSurfaceImage />
          <InputDiv>
            <span>a (m)</span>
            <input
              type="number"
              value={a}
              onChange={(event) => setA(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
          <InputDiv>
            <span>b (m)</span>
            <input
              type="number"
              value={b}
              onChange={(event) => setB(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
        </>
      )
    } else if (selectedAreaType == areasType[1]) {
      return (
        <>
          <RoofSurfaceImage />
          <InputDiv>
            <span>a (m)</span>
            <input
              type="number"
              value={a}
              onChange={(event) => setA(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
          <InputDiv>
            <span>b (m)</span>
            <input
              type="number"
              value={b}
              onChange={(event) => setB(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
          <InputDiv>
            <span>h (m)</span>
            <input
              type="number"
              value={h}
              onChange={(event) => setH(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
        </>
      )
    } else if (selectedAreaType == areasType[2]) {
      return (
        <>
          <InputDiv>
            <span>Área (m²)</span>
            <input
              type="number"
              value={manualArea}
              onChange={(event) => setManualArea(Number(event.target.value))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleAddArea(e)
                }
              }}
            />
          </InputDiv>
        </>
      )
    }
  })()

  return (
    <PageContainer>
      <PageHeader $gradient={headerGradient}>
        <div className="header-content">
          <BackButton to="/hidrossanitario">
            <FiArrowLeft /> Voltar para Hidrossanitário
          </BackButton>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span>/</span>
            <a href="/hidrossanitario">Hidrossanitário</a>
            <span>/</span>
            <span>Águas pluviais</span>
          </div>
          <h1>Dimensionamento de águas pluviais</h1>
          <p className="subtitle">
            Calcule áreas de contribuição, condutores, calhas e peças considerando a intensidade de chuva e a geometria
            da cobertura.
          </p>
          <div className="badges">
            <Badge>NBR 10844</Badge>
            <Badge>Áreas de contribuição</Badge>
            <Badge>Calhas e condutores</Badge>
          </div>
        </div>
      </PageHeader>

      <InfoCard
        $background="linear-gradient(135deg, #ecfeff 0%, #cffafe 100%)"
        $border="2px solid #06b6d4"
        $primaryColor="#0e7490"
      >
        <div className="info-content">
          <div className="icon">
            <FiInfo />
          </div>
          <div className="text">
            <h3>Sobre esta calculadora</h3>
            <p>
              Defina áreas horizontais ou inclinadas e vincule-as a calhas e condutores verticais/horizontais para medir
              a demanda.
            </p>
            <p>
              Ajuste diâmetros e declividades conforme catálogos ou valores próprios e compare com a capacidade
              calculada para cada trecho.
            </p>
            <div className="norms">
              <span>NBR 10844</span>
              <span>Rede pluvial predial</span>
            </div>
          </div>
        </div>
      </InfoCard>

      <Container>
        <Card>
          <RateLimitIndicator />

          <h2>Águas pluviais</h2>
          <form onSubmit={handleCalculate}>
            <h3>Dados de uso</h3>
            <InputDiv>
              <span>Intensidade pluviométrica (mm/h)</span>
              <input type="number" value={rain || ''} onChange={(event) => setRain(Number(event.target.value))} />
            </InputDiv>
            <h3>Áreas de contribuição</h3>
            <InputDiv>
              <span>Nome</span>
              <div>
                <input
                  type="text"
                  value={areaName}
                  onChange={(event) => setAreaName(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddArea(e)
                    }
                  }}
                />
                <AddButton type="button" onClick={handleAddArea}>
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Tipo</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="areaTypeSelect"
                  value={selectedAreaType}
                  onChange={(event) => setSelectedAreaType(event.target.value)}
                >
                  {areasType.map((area, index) => (
                    <option key={index} value={area}>
                      {area}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {areaCalculator}
            {areas.length >= 1 && (
              <TableContainer>
                <Table style={{ marginTop: '20px' }}>
                  <thead>
                    <Tr>
                      <Th>Número</Th>
                      <Th>Nome</Th>
                      <Th>Área (m²)</Th>
                      <Th>Adicionar à elemento</Th>
                      <Th>Editar</Th>
                      <Th>Remover</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {areas.map((a, index) => (
                      <Tr
                        data-tooltip-id="standard-tooltip"
                        data-tooltip-content={`Clique para copiar o nome para lançamento`}
                        onClick={() => {
                          setAreaName(a.name)
                        }}
                        key={index}
                      >
                        <Td>A{a.id}</Td>
                        <Td>{a.name}</Td>
                        <Td>{a.area}</Td>
                        <Td>
                          {gutters.map((g, index) => (
                            <Chip
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar a área A${a.id} à calha C${g.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddAreaContributionToGutter(event, g.id, a.id)}
                              $clickable
                            >
                              C{g.id}
                            </Chip>
                          ))}
                          {verticals.map((v, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar a área A${a.id} ao condutor vertical CV${v.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddAreaContributionToVertical(event, v.id, a.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CV{v.id}
                            </span>
                          ))}
                          {horizontals.map((h, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar a área A${a.id} ao condutor horizontal CH${h.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddAreaContributionToHorizontal(event, h.id, a.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CH{h.id}
                            </span>
                          ))}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button edit" onClick={(event) => handleEditArea(event, a.id)}>
                            <FaEdit />
                          </button>
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button remove" onClick={(event) => handleRemoveArea(event, a.id)}>
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Tooltip id="standard-tooltip" style={{ whiteSpace: 'pre-line' }} />
              </TableContainer>
            )}
            <h3>Calhas</h3>
            <InputDiv>
              <span>Nome</span>
              <div>
                <input
                  type="text"
                  value={gutterName}
                  onChange={(event) => setGutterName(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddGutter(e)
                    }
                  }}
                />
                <AddButton type="button" onClick={handleAddGutter}>
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Tipo</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="gutterSelect"
                  value={selectedGutterType}
                  onChange={(event) => setSelectedGutterType(event.target.value)}
                >
                  {gutterTypes.map((gutter, index) => (
                    <option key={index} value={gutter}>
                      {gutter}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {gutterTypes[0] === selectedGutterType ? (
              <>
                <InputDiv>
                  <span>Material</span>
                  <div>
                    <Select
                      style={{ marginLeft: '0' }}
                      id="materialSelect"
                      value={selectedMaterial}
                      onChange={(event) => setSelectedMaterial(event.target.value)}
                    >
                      {materials.map((m, index) => (
                        <option key={index} value={m.name}>
                          {m.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                </InputDiv>
                <InputDiv>
                  <span>Inclinação (%)</span>
                  <input
                    type="number"
                    value={gutterSlope}
                    onChange={(event) => setGutterSlope(Number(event.target.value))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddGutter(e)
                      }
                    }}
                  />
                </InputDiv>
              </>
            ) : (
              <InputDiv>
                <span>Inclinação</span>
                <div>
                  <Select
                    style={{ marginLeft: '0' }}
                    id="semiCircularGutterSlopeSelect"
                    value={gutterSlope}
                    onChange={(event) => setGutterSlope(event.target.value)}
                  >
                    {uniqueGutterSlopes.map((s, index) => (
                      <option key={index} value={s}>
                        {s}
                      </option>
                    ))}
                  </Select>
                </div>
              </InputDiv>
            )}
            {gutterDimensionsDiv}
            {gutters.length >= 1 && (
              <TableContainer>
                <Table style={{ marginTop: '20px' }}>
                  <thead>
                    <Tr>
                      <Th>ID</Th>
                      <Th>Nome</Th>
                      <Th>i</Th>
                      <Th>Dimensões</Th>
                      <Th>Contribuições</Th>
                      <Th>Adicionar à elemento</Th>
                      <Th>Editar</Th>
                      <Th>Remover</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {gutters.map((g, index) => (
                      <Tr
                        data-tooltip-id="standard-tooltip"
                        data-tooltip-content={`Mais informações sobre C${g.id}\nMaterial: ${g.material.name}\nClique para copiar os dados para lançamento`}
                        onClick={() => {
                          setGutterName(g.name)
                          setSelectedMaterial(g.material.name)
                          setGutterSlope(g.slope)
                          setSelectedGutterType(g.type)
                          if (g.type === gutterTypes[0]) {
                            setGutterB(g.dimensions.b)
                            setGutterH(g.dimensions.h)
                          } else {
                            setGutterD(g.dimensions.d)
                          }
                        }}
                        key={index}
                      >
                        <Td>C{g.id}</Td>
                        <Td>{g.name}</Td>
                        {g.type === gutterTypes[0] ? <Td>{g.slope}%</Td> : <Td>{g.slope}</Td>}
                        <Td>{g.dimension}</Td>
                        <Td>
                          {g.contributions.areasContributions.map((ac, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover a área A${ac.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveAreaContributionFromGutter(event, g.id, ac.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              A{ac.id}
                            </span>
                          ))}
                        </Td>
                        <Td>
                          {verticals.map((v, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar a calha C${g.id} ao condutor vertical CV${v.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddGutterContributionToVertical(event, v.id, g.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CV{v.id}
                            </span>
                          ))}
                          {horizontals.map((h, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar a calha C${g.id} ao condutor horizontal CH${h.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddGutterContributionToHorizontal(event, h.id, g.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CH{h.id}
                            </span>
                          ))}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button edit" onClick={(event) => handleEditGutter(event, g.id)}>
                            <FaEdit />
                          </button>
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button remove" onClick={(event) => handleRemoveGutter(event, g.id)}>
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
                <Tooltip id="standard-tooltip" style={{ whiteSpace: 'pre-line' }} />
              </TableContainer>
            )}
            <h3>Condutores verticais</h3>
            <InputDiv>
              <span>Nome</span>
              <div>
                <input
                  type="text"
                  value={verticalName}
                  onChange={(event) => setVerticalName(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddVertical(e)
                    }
                  }}
                />
                <AddButton type="button" onClick={handleAddVertical}>
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Quantidade</span>
              <input
                type="number"
                value={verticalQuantity}
                onChange={(event) => setVerticalQuantity(Number(event.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddVertical(e)
                  }
                }}
              />
            </InputDiv>
            <InputDiv>
              <span>Diâmetro</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="verticalDiameterSelect"
                  value={verticalDiameter}
                  onChange={(event) => setVerticalDiameter(event.target.value)}
                >
                  {uniqueHorizontalDiameters.map((d, index) => (
                    <option key={index} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {verticals.length >= 1 && (
              <TableContainer>
                <Table style={{ marginTop: '20px' }}>
                  <thead>
                    <Tr>
                      <Th>Número</Th>
                      <Th>Nome</Th>
                      <Th>Diâmetro (mm)</Th>
                      <Th>Contribuições</Th>
                      <Th>Adicionar à elemento</Th>
                      <Th>Editar</Th>
                      <Th>Remover</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {verticals.map((v, index) => (
                      <Tr
                        data-tooltip-id="standard-tooltip"
                        data-tooltip-content={`Mais informações sobre CV${v.id}\nQuantidade: ${v.quantity}\nClique para copiar os dados para lançamento`}
                        onClick={() => {
                          setVerticalName(v.name)
                          setVerticalQuantity(v.quantity)
                          setVerticalDiameter(v.diameter)
                        }}
                        key={index}
                      >
                        <Td>CV{v.id}</Td>
                        <Td>{v.name}</Td>
                        <Td>{v.diameter}</Td>
                        <Td>
                          {v.contributions.areasContributions.map((ac, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover a área A${ac.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveAreaContributionFromVertical(event, v.id, ac.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              A{ac.id}
                            </span>
                          ))}
                          {v.contributions.gutterContributions.map((gc, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover a calha C${gc.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveGutterContributionFromVertical(event, v.id, gc.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              C{gc.id}
                            </span>
                          ))}
                        </Td>
                        <Td>
                          {horizontals.map((h, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Adicionar o condutor vertical CV${v.id} ao condutor horizontal CH${h.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleAddVerticalContributionToHorizontal(event, h.id, v.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CH{h.id}
                            </span>
                          ))}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button edit" onClick={(event) => handleEditVertical(event, v.id)}>
                            <FaEdit />
                          </button>
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button remove" onClick={(event) => handleRemoveVertical(event, v.id)}>
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
            <h3>Condutores horizontais</h3>
            <InputDiv>
              <span>Nome</span>
              <div>
                <input
                  type="text"
                  value={horizontalName}
                  onChange={(event) => setHorizontalName(event.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddHorizontal(e)
                    }
                  }}
                />
                <AddButton type="button" onClick={handleAddHorizontal}>
                  <FaPlusCircle />
                </AddButton>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Quantidade</span>
              <input
                type="number"
                value={horizontalQuantity}
                onChange={(event) => setHorizontalQuantity(Number(event.target.value))}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    handleAddHorizontal(e)
                  }
                }}
              />
            </InputDiv>
            <InputDiv>
              <span>Inclinação</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="circularHorizontalSlopeSelect"
                  value={horizontalSlope}
                  onChange={(event) => setHorizontalSlope(event.target.value)}
                >
                  {uniqueHorizontalSlopes.map((s, index) => (
                    <option key={index} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            <InputDiv>
              <span>Diâmetro</span>
              <div>
                <Select
                  style={{ marginLeft: '0' }}
                  id="horizontalDiameterSelect"
                  value={horizontalDiameter}
                  onChange={(event) => setHorizontalDiameter(event.target.value)}
                >
                  {uniqueHorizontalDiameters.map((d, index) => (
                    <option key={index} value={d}>
                      {d}
                    </option>
                  ))}
                </Select>
              </div>
            </InputDiv>
            {horizontals.length >= 1 && (
              <TableContainer>
                <Table style={{ marginTop: '20px' }}>
                  <thead>
                    <Tr>
                      <Th>Número</Th>
                      <Th>Nome</Th>
                      <Th>i</Th>
                      <Th>Diâmetro (mm)</Th>
                      <Th>Contribuições</Th>
                      <Th>Editar</Th>
                      <Th>Remover</Th>
                    </Tr>
                  </thead>
                  <tbody>
                    {horizontals.map((h, index) => (
                      <Tr
                        data-tooltip-id="standard-tooltip"
                        data-tooltip-content={`Mais informações sobre CH${h.id}\nQuantidade: ${h.quantity}\nClique para copiar os dados para lançamento`}
                        onClick={() => {
                          setHorizontalName(h.name)
                          setHorizontalQuantity(h.quantity)
                          setHorizontalSlope(h.slope)
                          setHorizontalDiameter(h.diameter)
                        }}
                        key={index}
                      >
                        <Td>CH{h.id}</Td>
                        <Td>{h.name}</Td>
                        <Td>{h.slope}</Td>
                        <Td>{h.diameter}</Td>
                        <Td>
                          {h.contributions.areasContributions.map((c, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover a área A${c.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveAreaContributionFromHorizontal(event, h.id, c.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              A{c.id}
                            </span>
                          ))}
                          {h.contributions.gutterContributions.map((gc, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover a calha C${gc.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveGutterContributionFromHorizontal(event, h.id, gc.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              C{gc.id}
                            </span>
                          ))}
                          {h.contributions.verticalContributions.map((vc, index) => (
                            <span
                              data-tooltip-id="standard-tooltip"
                              data-tooltip-content={`Remover o condutor vertical CV${vc.id}`}
                              data-tooltip-place="bottom"
                              key={index}
                              onClick={(event) => handleRemoveVerticalContributionFromHorizontal(event, h.id, vc.id)}
                              style={{
                                display: 'inline-block',
                                backgroundColor: '#f0f0f0',
                                borderRadius: '12px',
                                padding: '4px 10px',
                                marginRight: '6px',
                                marginBottom: '4px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                border: '1px solid #ccc',
                              }}
                            >
                              CV{vc.id}
                            </span>
                          ))}
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button className="icon-button edit" onClick={(event) => handleEditHorizontal(event, h.id)}>
                            <FaEdit />
                          </button>
                        </Td>
                        <Td style={{ textAlign: 'center' }}>
                          <button
                            className="icon-button remove"
                            onClick={(event) => handleRemoveHorizontal(event, h.id)}
                          >
                            <FaMinusCircle />
                          </button>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableContainer>
            )}
            <ActionButtonsRow style={{ marginTop: horizontals.length > 0 ? 0 : 20 }}>
              <button type="button" onClick={handleFileSave}>
                Salvar
              </button>
              <button type="button" onClick={() => document.getElementById('file-input').click()}>
                Carregar
              </button>
              <button type="submit" disabled={!isFormValid || !canCalculate}>
                {!canCalculate ? 'Limite atingido' : 'Calcular'}
              </button>
            </ActionButtonsRow>
            <input type="file" id="file-input" onChange={handleFileUpload} style={{ display: 'none' }} />
          </form>
        </Card>
        {result !== '' && (
          <Result>
            <h2>Resultados</h2>
            <TableContainer>
              <Table>
                <thead>
                  <Tr>
                    <Th>Número</Th>
                    <Th>Nome</Th>
                    <Th>Demanda (L/min)</Th>
                    <Th>Capacidade (L/min)</Th>
                    <Th>Percentual</Th>
                  </Tr>
                </thead>
                <tbody>
                  {result.resultGutters.map((g) => (
                    <Tr key={g.id}>
                      <Td>C{g.id}</Td>
                      <Td>{g.name}</Td>
                      <Td>{g.request.toFixed(1)}</Td>
                      <Td>{g.result.q.toFixed(1)}</Td>
                      <Td
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: `linear-gradient(to top,${Math.round(g.ratio * 100) <= 100 ? 'lightblue' : 'lightcoral'} ${Math.min(Math.round(g.ratio * 100), 100)}%,transparent ${Math.min(Math.round(g.ratio * 100), 100)}%)`,
                        }}
                      >
                        {`${Math.round(g.ratio * 100)}%`}
                      </Td>
                    </Tr>
                  ))}
                  {result.resultVerticals.map((v) => (
                    <Tr key={v.id}>
                      <Td>CV{v.id}</Td>
                      <Td>{v.name}</Td>
                      <Td>{v.request.toFixed(1)}</Td>
                      <Td>{v.result.toFixed(1)}</Td>
                      <Td
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: `linear-gradient(to top,${Math.round(v.ratio * 100) <= 100 ? 'lightblue' : 'lightcoral'} ${Math.min(Math.round(v.ratio * 100), 100)}%,transparent ${Math.min(Math.round(v.ratio * 100), 100)}%)`,
                        }}
                      >
                        {`${Math.round(v.ratio * 100)}%`}
                      </Td>
                    </Tr>
                  ))}
                  {result.resultHorizontals.map((h) => (
                    <Tr key={h.id}>
                      <Td>CH{h.id}</Td>
                      <Td>{h.name}</Td>
                      <Td>{h.request.toFixed(1)}</Td>
                      <Td>{h.result.q.toFixed(1)}</Td>
                      <Td
                        style={{
                          position: 'relative',
                          overflow: 'hidden',
                          background: `linear-gradient(to top,${Math.round(h.ratio * 100) <= 100 ? 'lightblue' : 'lightcoral'} ${Math.min(Math.round(h.ratio * 100), 100)}%,transparent ${Math.min(Math.round(h.ratio * 100), 100)}%)`,
                        }}
                      >
                        {`${Math.round(h.ratio * 100)}%`}
                      </Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </TableContainer>
          </Result>
        )}
      </Container>
    </PageContainer>
  )
}
